import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from '@mui/icons-material/FolderOutlined';
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { useTranslation } from "react-i18next";
import { useGetMenu, useUpdateMenu, useDeleteMenuItem } from "src/data/restaurant/menu";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import { CustomButton, TipsCard } from "src/design";
import {
  colorTextMuted,
  colorSurface,
  colorBorder,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { MenuItem, MenuSection } from "src/business/restaurant/menu";
import { buildPublicMenuUrl } from "src/business/restaurant/publicMenuUrl";
import { MenuStatsRow } from "./components/MenuStatsRow";
import { MenuSearchBar } from "./components/MenuSearchBar";
import { MenuItemsList } from "./components/MenuItemsList";
import { AddMenuItemModal, MenuItemFormData } from "./components/AddMenuItemModal";
import { AddCategoryModal } from "./components/AddCategoryModal";
import { MenuQrCodeModal } from "./components/MenuQrCodeModal";

export const Menu = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { t } = useTranslation();

  const { menu, refetch } = useGetMenu(restaurantId ?? "");
  const { updateMenu, loading: saving } = useUpdateMenu(restaurantId ?? "");
  const { deleteMenuItem } = useDeleteMenuItem(restaurantId ?? "");

  const { user } = useContext(UserContext);
  const { restaurants } = useContext(RestaurantContext);
  const restaurant = restaurants.find((r) => r.id === restaurantId);
  const isMenuUpdateEnabled = isFeatureSectionEnabled(
    ["menu_update"],
    "menu_management",
    user,
    restaurant?.capabilities.map((c) => c.name),
  );

  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{ item: MenuItem; sectionName: string } | undefined>();
  const [localCategories, setLocalCategories] = useState<string[]>([]);

  if (!restaurantId) return null;

  const allSections: MenuSection[] = [
    ...menu,
    ...localCategories
      .filter((c) => !menu.some((s) => s.section === c))
      .map((c) => ({ section: c, active: true, menuItems: [] })),
  ];

  const handleOpenAddItem = () => {
    setEditTarget(undefined);
    setItemModalOpen(true);
  };

  const handleEdit = (item: MenuItem, sectionName: string) => {
    setEditTarget({ item, sectionName });
    setItemModalOpen(true);
  };

  const handleSubmitItem = async (data: MenuItemFormData) => {
    if (data.existingId) {
      await updateMenu({
        menusToUpdate: [{
          id: data.existingId,
          name: data.name,
          description: data.description,
          price: data.price,
          featured: false,
        }],
        menusToCreate: [],
      });
    } else {
      const targetSection = allSections.find((s) => s.section === data.category);
      const items = targetSection?.menuItems ?? [];
      const lastPriority = items.length > 0
        ? Math.max(...items.map((i) => i.priority))
        : -1;
      await updateMenu({
        menusToCreate: [{
          id: null,
          item: data.name,
          section: data.category,
          description: data.description,
          price: data.price,
          featured: false,
          priority: lastPriority,
        }],
        menusToUpdate: [],
      });
    }
    setItemModalOpen(false);
    setEditTarget(undefined);
    refetch();
  };

  const handleReorder = async (updates: { id: string; priority: number }[]) => {
    await updateMenu({
      menusToUpdate: updates,
      menusToCreate: [],
    });
    refetch();
  };

  const handleAddCategory = (name: string) => {
    setLocalCategories((prev) => [...prev, name]);
  };

  const handleDelete = async (itemId: string | null) => {
    if (!itemId) return;
    await deleteMenuItem(itemId);
    refetch();
  };

  const handleToggleFeatured = async (item: MenuItem) => {
    if (!item.id) return;
    await updateMenu({
      menusToUpdate: [{ id: item.id, featured: !item.featured }],
      menusToCreate: [],
    });
    refetch();
  };

  const visibleSections = allSections.length > 0
    ? allSections
    : [{ section: "", active: true, menuItems: [] }];

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "24px", lineHeight: 1.3 }}>
            {t("menu.pageTitle")}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: colorTextMuted, mt: 0.5 }}>
            {t("menu.pageSubtitle")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {restaurant && (
            <CustomButton
              color="inverted"
              variant="outlined"
              startIcon={<QrCode2Icon />}
              onClick={() => setQrModalOpen(true)}
            >
              {t("menu.shareQr")}
            </CustomButton>
          )}
          {isMenuUpdateEnabled && (
            <>
              <CustomButton
                color="inverted"
                variant="outlined"
                startIcon={<FolderIcon />}
                onClick={() => setCategoryModalOpen(true)}
              >
                {t("menu.addCategory")}
              </CustomButton>
              <CustomButton
                color="black"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddItem}
              >
                {t("menu.addItem")}
              </CustomButton>
            </>
          )}
        </Box>
      </Box>

      <MenuStatsRow menu={menu} />

      <MenuSearchBar
        search={search}
        onSearchChange={setSearch}
        selectedCategories={selectedCategories}
        onCategoryChange={setSelectedCategories}
        sections={menu}
      />

      <Box
        sx={{
          backgroundColor: colorSurface,
          border: `1px solid ${colorBorder}`,
          borderRadius: radiusCard,
          boxShadow: shadowCard,
          px: { xs: 2, sm: 3 },
          py: 1,
        }}
      >
        <MenuItemsList
          menu={menu}
          search={search}
          selectedCategories={selectedCategories}
          canUpdate={isMenuUpdateEnabled}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleFeatured={handleToggleFeatured}
          onReorder={handleReorder}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <TipsCard
          title={t("menu.tips.title")}
          tips={[
            t("menu.tips.featured"),
            t("menu.tips.reorder"),
            t("menu.tips.descriptions"),
            t("menu.tips.freshness"),
          ]}
        />
      </Box>

      <AddMenuItemModal
        key={`${itemModalOpen}-${editTarget?.item.id ?? "new"}`}
        open={itemModalOpen}
        onClose={() => { setItemModalOpen(false); setEditTarget(undefined); }}
        sections={visibleSections}
        onSubmit={handleSubmitItem}
        loading={saving}
        initialData={editTarget}
      />

      <AddCategoryModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onSubmit={handleAddCategory}
      />

      {restaurant && (
        <MenuQrCodeModal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          url={buildPublicMenuUrl(restaurant.name, restaurant.city)}
          restaurantName={restaurant.name}
        />
      )}
    </Box>
  );
};
